import React, { useCallback, useEffect, useId, useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { Button, Input, LabelChip } from '../shared/ui';
import { createLabel, deleteLabel, listLabels, updateLabel } from '../services/labels';
import styles from './ManageLabelsModal.module.css';

const COLOR_OPTIONS = ['neutral', 'brand', 'success', 'info', 'warning', 'danger', 'accent'];
const KIND_OPTIONS = [
  { id: 'both', label: 'Families & students' },
  { id: 'family', label: 'Families only' },
  { id: 'student', label: 'Students only' }
];

function formatColorOption(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function ManageLabelsModal({ open, onClose, onChanged }) {
  const formId = useId();
  const colorSelectId = `${formId}-color`;
  const kindSelectId = `${formId}-kind`;
  const listNameHeaderId = `${formId}-list-col-name`;

  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ name: '', color: 'neutral', kind: 'both' });
  /** In-progress label name edits; cleared on blur. Value falls back to server `labels[].name`. */
  const [pendingNames, setPendingNames] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingLabel, setDeletingLabel] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listLabels();
      setLabels(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError('');
      refresh();
    }
  }, [open, refresh]);

  useEffect(() => {
    const ids = new Set(labels.map((l) => l.id));
    setPendingNames((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(next)) {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [labels]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setError('');
    try {
      const created = await createLabel(draft);
      setLabels((list) => [...list, created].sort((a, b) => a.name.localeCompare(b.name)));
      setDraft({ name: '', color: 'neutral', kind: 'both' });
      onChanged && onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleUpdate = async (id, patch) => {
    try {
      const updated = await updateLabel(id, patch);
      setLabels((list) => list.map((l) => (l.id === id ? updated : l)));
      onChanged && onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const confirmDeleteLabel = async () => {
    if (!deleteTarget) return;
    setDeletingLabel(true);
    setError('');
    try {
      await deleteLabel(deleteTarget.id);
      setLabels((list) => list.filter((l) => l.id !== deleteTarget.id));
      onChanged && onChanged();
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingLabel(false);
    }
  };

  const requestDeleteLabel = (id) => {
    const label = labels.find((l) => l.id === id);
    setDeleteTarget({ id, name: label?.name?.trim() || 'this label' });
  };

  const commitLabelNameIfChanged = async (id, label) => {
    const pending = pendingNames[id];
    setPendingNames((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    const raw = pending !== undefined ? pending : label.name;
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    if (!trimmed || trimmed === label.name.trim()) return;
    await handleUpdate(id, { name: trimmed });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="Manage labels"
      description="Tag families and students for fast filtering."
      size="lg"
    >
      <div className={styles.wrap}>
        <section className={styles.composer} aria-labelledby={`${formId}-composer-title`}>
          <div className={styles.composerIntro}>
            <h4 id={`${formId}-composer-title`} className={styles.composerTitle}>
              Create a label
            </h4>
            <p className={styles.composerHint}>Choose a color and where the tag can be used.</p>
          </div>
          <form onSubmit={handleCreate} className={styles.createForm}>
            <Input
              label="Name"
              placeholder="e.g. Transportation"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              autoComplete="off"
              className={styles.nameCell}
            />
            <div className={styles.selectField}>
              <label className={styles.miniLabel} htmlFor={colorSelectId}>
                Color
              </label>
              <select
                id={colorSelectId}
                className={styles.select}
                value={draft.color}
                onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
              >
                {COLOR_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {formatColorOption(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.selectField}>
              <label className={styles.miniLabel} htmlFor={kindSelectId}>
                Show on
              </label>
              <select
                id={kindSelectId}
                className={styles.select}
                value={draft.kind}
                onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}
              >
                {KIND_OPTIONS.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.submitCell}>
              <Button type="submit" iconLeft={<FaPlus />}>
                Add label
              </Button>
            </div>
          </form>
        </section>

        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}

        <section className={styles.listSection} aria-labelledby={`${formId}-list-title`}>
          <div className={styles.listToolbar}>
            <h4 id={`${formId}-list-title`} className={styles.listTitle}>
              Your labels
            </h4>
            {!loading ? <span className={styles.countBadge}>{labels.length}</span> : null}
          </div>

          {!loading && labels.length > 0 ? (
            <div className={styles.columnHeaders}>
              <span>Preview</span>
              <span id={listNameHeaderId}>Name</span>
              <span>Color</span>
              <span>Show on</span>
              <span className={styles.srOnly}>Actions</span>
            </div>
          ) : null}

          <div className={styles.list}>
            {loading ? (
              <div className={styles.loading}>
                <span className={styles.loadingPulse} />
                Loading labels…
              </div>
            ) : labels.length === 0 ? (
              <div className={styles.empty}>
                <p className={styles.emptyTitle}>No labels yet</p>
                <p className={styles.emptyHint}>Create one above — it will show up in filters and on rows.</p>
              </div>
            ) : (
              labels.map((l) => (
                <div key={l.id} className={styles.row}>
                  <div className={styles.preview}>
                    <LabelChip
                      label={pendingNames[l.id] !== undefined ? pendingNames[l.id] : l.name}
                      color={l.color}
                      size="md"
                    />
                  </div>
                  <Input
                    className={styles.rowNameInput}
                    aria-labelledby={listNameHeaderId}
                    value={pendingNames[l.id] !== undefined ? pendingNames[l.id] : l.name}
                    onChange={(e) =>
                      setPendingNames((prev) => ({ ...prev, [l.id]: e.target.value }))
                    }
                    onBlur={() => commitLabelNameIfChanged(l.id, l)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    autoComplete="off"
                  />
                  <div className={styles.rowFields}>
                    <select
                      className={styles.select}
                      value={l.color}
                      onChange={(e) => handleUpdate(l.id, { color: e.target.value })}
                      aria-label={`Color for ${l.name}`}
                    >
                      {COLOR_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {formatColorOption(c)}
                        </option>
                      ))}
                    </select>
                    <select
                      className={styles.select}
                      value={l.kind}
                      onChange={(e) => handleUpdate(l.id, { kind: e.target.value })}
                      aria-label={`Show ${l.name} on`}
                    >
                      {KIND_OPTIONS.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className={styles.delete}
                    onClick={() => requestDeleteLabel(l.id)}
                    aria-label={`Delete ${l.name}`}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </Modal>
    <ConfirmDialog
      open={deleteTarget != null}
      onClose={() => {
        if (!deletingLabel) setDeleteTarget(null);
      }}
      onConfirm={confirmDeleteLabel}
      title="Delete this label?"
      description={
        deleteTarget
          ? `“${deleteTarget.name.replace(/"/g, "'")}” will be removed from all families and students. This cannot be undone.`
          : ''
      }
      confirmLabel="Delete label"
      cancelLabel="Cancel"
      variant="danger"
      loading={deletingLabel}
    />
    </>
  );
}
